/**
 * Audio Capture Module
 *
 * Handles microphone capture with AudioWorklet (primary) and
 * ScriptProcessorNode (fallback) for browser compatibility.
 *
 * Features:
 * - AudioWorklet for low-latency capture
 * - Automatic fallback for older browsers
 * - Volume level tracking for UI visualization
 * - Clean resource disposal
 *
 * @module gemini/audio-capture
 */

import { AudioCaptureConfig, CaptureMode } from './types';

/**
 * Default capture configuration
 */
const DEFAULT_CONFIG: AudioCaptureConfig = {
  targetSampleRate: 16000,
  bufferDurationMs: 100,
  enableRecording: true,
};

/**
 * Watchdog configuration for audio fallback detection
 */
const WATCHDOG_CONFIG = {
  /** Check interval in milliseconds */
  checkIntervalMs: 200,
  /** Warn after this much silence (milliseconds) */
  silenceWarningMs: 500,
  /** Consider degraded after this much silence (milliseconds) */
  degradedThresholdMs: 1000,
  /** Recovery threshold - need activity for this long to recover (milliseconds) */
  recoveryThresholdMs: 2000,
};

/**
 * Audio processor message from worklet
 */
interface AudioProcessorMessage {
  type: 'initialized' | 'audio-chunk' | 'volume' | 'recording-complete' | 'buffer-cleared' | 'recording-started';
  data?: ArrayBuffer;
  level?: number;
  config?: {
    targetSampleRate: number;
    bufferDurationMs: number;
    targetBufferSize: number;
  };
  sampleRate?: number;
  samples?: number;
}

/**
 * Audio capture state for watchdog
 */
export type AudioCaptureState = 'healthy' | 'warning' | 'degraded';

/**
 * Degraded state info for UI feedback
 */
export interface DegradedStateInfo {
  state: AudioCaptureState;
  silenceDurationMs: number;
  captureMode: CaptureMode;
}

/**
 * Callback types for audio capture events
 */
export interface AudioCaptureCallbacks {
  /** Called when audio chunk is ready for transmission */
  onAudioChunk: (pcmData: ArrayBuffer) => void;
  /** Called with volume level updates (0-1) */
  onVolumeLevel?: (level: number) => void;
  /** Called when recording data is available */
  onRecordingData?: (data: ArrayBuffer) => void;
  /** Called on error */
  onError?: (error: Error) => void;
  /** Called when audio capture state changes (healthy/warning/degraded) */
  onStateChange?: (info: DegradedStateInfo) => void;
}

/**
 * Audio Capture Class
 *
 * Manages microphone input with automatic fallback strategy:
 * 1. AudioWorklet (best quality, lowest latency)
 * 2. ScriptProcessorNode (deprecated but widely supported)
 */
export class AudioCapture {
  private config: AudioCaptureConfig;
  private callbacks: AudioCaptureCallbacks;
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private scriptProcessor: ScriptProcessorNode | null = null;
  private captureMode: CaptureMode = 'worklet';
  private isCapturing = false;
  private isMuted = false;

  // For ScriptProcessorNode fallback
  private buffer: Float32Array = new Float32Array(0);
  private targetBufferSize = 0;

  // Watchdog state for audio fallback detection
  private watchdogInterval: ReturnType<typeof setInterval> | null = null;
  private lastMessageTime = 0;
  private currentState: AudioCaptureState = 'healthy';
  private stateChangeTime = 0;

  constructor(config: Partial<AudioCaptureConfig> = {}, callbacks: AudioCaptureCallbacks) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.callbacks = callbacks;

    // Calculate target buffer size
    this.targetBufferSize = Math.floor(
      (this.config.targetSampleRate * this.config.bufferDurationMs) / 1000
    );
  }

  /**
   * Initialize audio capture
   * Requests microphone permission and sets up audio pipeline
   */
  async initialize(): Promise<CaptureMode> {
    try {
      // Request microphone permission
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });

      // Create AudioContext
      this.audioContext = new AudioContext();

      // Create source node from microphone
      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);

      // Try AudioWorklet first
      this.captureMode = await this.initializeCapture();

      console.log(`[AudioCapture] Initialized with mode: ${this.captureMode}`);
      return this.captureMode;
    } catch (error) {
      console.error('[AudioCapture] Initialization failed:', error);
      this.callbacks.onError?.(error instanceof Error ? error : new Error('Failed to initialize audio capture'));
      throw error;
    }
  }

  /**
   * Attempt to initialize AudioWorklet, fallback to ScriptProcessor
   */
  private async initializeCapture(): Promise<CaptureMode> {
    if (!this.audioContext || !this.sourceNode) {
      throw new Error('AudioContext not initialized');
    }

    // Try AudioWorklet first (modern browsers)
    if ('audioWorklet' in this.audioContext) {
      try {
        await this.audioContext.audioWorklet.addModule('/workers/audio-processor.js');

        this.workletNode = new AudioWorkletNode(this.audioContext, 'audio-processor', {
          processorOptions: {
            targetSampleRate: this.config.targetSampleRate,
            bufferDurationMs: this.config.bufferDurationMs,
            enableRecording: this.config.enableRecording,
          },
        });

        // Handle messages from worklet
        this.workletNode.port.onmessage = (event: MessageEvent<AudioProcessorMessage>) => {
          this.handleWorkletMessage(event.data);
        };

        // Connect: microphone → worklet
        this.sourceNode.connect(this.workletNode);

        return 'worklet';
      } catch (error) {
        console.warn('[AudioCapture] AudioWorklet failed, falling back to ScriptProcessor:', error);
      }
    }

    // Fallback to ScriptProcessorNode (deprecated but works everywhere)
    console.warn('[AudioCapture] Using deprecated ScriptProcessorNode');

    const bufferSize = 4096; // Power of 2, reasonable latency
    this.scriptProcessor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);

    this.scriptProcessor.onaudioprocess = (event: AudioProcessingEvent) => {
      if (!this.isCapturing || this.isMuted) return;
      this.handleScriptProcessorAudio(event.inputBuffer.getChannelData(0));
    };

    // Connect: microphone → processor → destination (required for processing)
    this.sourceNode.connect(this.scriptProcessor);
    this.scriptProcessor.connect(this.audioContext.destination);

    return 'scriptProcessor';
  }

  /**
   * Handle messages from AudioWorklet
   */
  private handleWorkletMessage(message: AudioProcessorMessage): void {
    // Track message timing for watchdog
    this.lastMessageTime = Date.now();

    switch (message.type) {
      case 'audio-chunk':
        if (message.data && this.isCapturing && !this.isMuted) {
          this.callbacks.onAudioChunk(message.data);
        }
        break;

      case 'volume':
        if (message.level !== undefined) {
          this.callbacks.onVolumeLevel?.(message.level);
        }
        break;

      case 'recording-complete':
        if (message.data) {
          this.callbacks.onRecordingData?.(message.data);
        }
        break;

      case 'initialized':
        console.log('[AudioCapture] Worklet initialized:', message.config);
        break;
    }
  }

  /**
   * Start the watchdog timer to monitor audio pipeline health
   */
  private startWatchdog(): void {
    this.stopWatchdog(); // Clear any existing watchdog
    this.lastMessageTime = Date.now();
    this.currentState = 'healthy';
    this.stateChangeTime = Date.now();

    this.watchdogInterval = setInterval(() => {
      if (!this.isCapturing) return;

      const now = Date.now();
      const silenceDuration = now - this.lastMessageTime;
      let newState: AudioCaptureState = 'healthy';

      // Determine state based on silence duration
      if (silenceDuration >= WATCHDOG_CONFIG.degradedThresholdMs) {
        newState = 'degraded';
      } else if (silenceDuration >= WATCHDOG_CONFIG.silenceWarningMs) {
        newState = 'warning';
      }

      // Handle state transitions
      if (newState !== this.currentState) {
        const previousState = this.currentState;

        // Check if recovering from degraded state
        if (previousState !== 'healthy' && newState === 'healthy') {
          // Only transition to healthy if sustained for recovery threshold
          const healthyDuration = now - this.stateChangeTime;
          if (healthyDuration < WATCHDOG_CONFIG.recoveryThresholdMs) {
            return; // Not ready to recover yet
          }
          console.log(
            `[AudioCapture] Health state: ${previousState} → ${newState} ` +
            `(mode: ${this.captureMode}, recovered after ${healthyDuration}ms)`
          );
        } else if (newState === 'warning') {
          console.warn(
            `[AudioCapture] Health state: ${previousState} → ${newState} ` +
            `(mode: ${this.captureMode}, ${silenceDuration}ms silence)`
          );
        } else if (newState === 'degraded') {
          console.error(
            `[AudioCapture] Health state: ${previousState} → ${newState} ` +
            `(mode: ${this.captureMode}, ${silenceDuration}ms silence) ` +
            `- DEGRADED: Consider fallback or user notification`
          );
        }

        this.currentState = newState;
        this.stateChangeTime = now;

        // Notify UI of state change
        this.callbacks.onStateChange?.({
          state: newState,
          silenceDurationMs: silenceDuration,
          captureMode: this.captureMode,
        });
      }
    }, WATCHDOG_CONFIG.checkIntervalMs);

    console.log('[AudioCapture] Watchdog started');
  }

  /**
   * Stop the watchdog timer
   */
  private stopWatchdog(): void {
    if (this.watchdogInterval) {
      clearInterval(this.watchdogInterval);
      this.watchdogInterval = null;
    }
  }

  /**
   * Get current audio capture health state
   */
  getHealthState(): AudioCaptureState {
    return this.currentState;
  }

  /**
   * Handle audio from ScriptProcessorNode (fallback)
   */
  private handleScriptProcessorAudio(inputSamples: Float32Array): void {
    if (!this.audioContext) return;

    // Track message timing for watchdog (also applies to ScriptProcessor)
    this.lastMessageTime = Date.now();

    // Downsample to target rate
    const downsampled = this.downsample(
      inputSamples,
      this.audioContext.sampleRate,
      this.config.targetSampleRate
    );

    // Append to buffer
    const newBuffer = new Float32Array(this.buffer.length + downsampled.length);
    newBuffer.set(this.buffer);
    newBuffer.set(downsampled, this.buffer.length);
    this.buffer = newBuffer;

    // Calculate and report volume
    const volume = this.calculateVolume(inputSamples);
    this.callbacks.onVolumeLevel?.(volume);

    // Send buffered chunks
    while (this.buffer.length >= this.targetBufferSize) {
      const chunk = this.buffer.slice(0, this.targetBufferSize);
      this.buffer = this.buffer.slice(this.targetBufferSize);

      // Convert to Int16 PCM
      const pcmData = this.floatTo16BitPCM(chunk);
      this.callbacks.onAudioChunk(pcmData.buffer as ArrayBuffer);
    }
  }

  /**
   * Downsample audio using linear interpolation
   */
  private downsample(input: Float32Array, sourceRate: number, targetRate: number): Float32Array {
    if (sourceRate === targetRate) return input;

    const ratio = sourceRate / targetRate;
    const outputLength = Math.floor(input.length / ratio);
    const output = new Float32Array(outputLength);

    for (let i = 0; i < outputLength; i++) {
      const srcIndex = i * ratio;
      const srcFloor = Math.floor(srcIndex);
      const srcCeil = Math.min(srcFloor + 1, input.length - 1);
      const fraction = srcIndex - srcFloor;

      output[i] = input[srcFloor]! * (1 - fraction) + input[srcCeil]! * fraction;
    }

    return output;
  }

  /**
   * Convert Float32 to Int16 PCM
   */
  private floatTo16BitPCM(float32: Float32Array): Int16Array {
    const int16 = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i++) {
      const sample = Math.max(-1, Math.min(1, float32[i]!));
      int16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    }
    return int16;
  }

  /**
   * Calculate RMS volume
   */
  private calculateVolume(samples: Float32Array): number {
    let sum = 0;
    for (let i = 0; i < samples.length; i++) {
      sum += samples[i]! * samples[i]!;
    }
    return Math.sqrt(sum / samples.length);
  }

  /**
   * Start capturing audio
   */
  start(): void {
    if (!this.audioContext) {
      throw new Error('AudioCapture not initialized. Call initialize() first.');
    }

    // Resume AudioContext if suspended (autoplay policy)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }

    this.isCapturing = true;
    this.startWatchdog();
    console.log('[AudioCapture] Started capturing');
  }

  /**
   * Stop capturing audio
   */
  stop(): void {
    this.isCapturing = false;
    this.stopWatchdog();

    // Request recording data from worklet
    if (this.workletNode && this.config.enableRecording) {
      this.workletNode.port.postMessage({ type: 'stop-recording' });
    }

    console.log('[AudioCapture] Stopped capturing');
  }

  /**
   * Start recording (separate from streaming)
   */
  startRecording(): void {
    if (this.workletNode) {
      this.workletNode.port.postMessage({ type: 'start-recording' });
    }
  }

  /**
   * Stop recording and get data
   */
  stopRecording(): void {
    if (this.workletNode) {
      this.workletNode.port.postMessage({ type: 'stop-recording' });
    }
  }

  /**
   * Mute/unmute microphone
   */
  setMuted(muted: boolean): void {
    this.isMuted = muted;

    // Also mute the actual media tracks
    if (this.mediaStream) {
      this.mediaStream.getAudioTracks().forEach((track) => {
        track.enabled = !muted;
      });
    }
  }

  /**
   * Get current mute state
   */
  getMuted(): boolean {
    return this.isMuted;
  }

  /**
   * Get current capture mode
   */
  getCaptureMode(): CaptureMode {
    return this.captureMode;
  }

  /**
   * Check if currently capturing
   */
  getIsCapturing(): boolean {
    return this.isCapturing;
  }

  /**
   * Get the raw MediaStream for external use (e.g., synchronized recording)
   * Returns the MediaStream from getUserMedia, which can be used to create
   * a MediaStreamAudioSourceNode in a different AudioContext.
   */
  getMediaStream(): MediaStream | null {
    return this.mediaStream;
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    this.isCapturing = false;
    this.stopWatchdog();

    // Disconnect nodes
    if (this.workletNode) {
      this.workletNode.disconnect();
      this.workletNode = null;
    }

    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    // Stop media tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    // Close AudioContext
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    // Clear buffer
    this.buffer = new Float32Array(0);

    console.log('[AudioCapture] Disposed');
  }
}

/**
 * Check if AudioWorklet is supported
 */
export function isAudioWorkletSupported(): boolean {
  return typeof AudioContext !== 'undefined' && 'audioWorklet' in AudioContext.prototype;
}

/**
 * Check if microphone access is available
 */
export async function checkMicrophonePermission(): Promise<PermissionState> {
  try {
    const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    return result.state;
  } catch {
    // Permissions API not supported, return unknown
    return 'prompt';
  }
}
